// levels/util.ts — small shared helpers every level leans on.
// The state machine owns act theming, strikes, and transitions (SPEC §11); a level only
// renders into its host and resolves a LevelResult. These helpers keep the abort wiring
// and the CAPTCHA-grid DOM out of each level file.

import { el } from "@/ui/chrome.ts";
import type { LevelContext, LevelResult } from "@/levels/types.ts";

/**
 * Run a level's interaction as a Promise, wired to ctx.signal.
 *
 * `setup(done)` builds the DOM and calls `done(result)` when the subject has answered.
 * It may return a cleanup function (clear timers / stop sampling); cleanup runs on either
 * resolution or abort. On abort the promise rejects with an AbortError, which the state
 * machine catches to honor a queued nav (debug step, appeal). `done` is idempotent.
 */
export function runLevel(
  ctx: LevelContext,
  setup: (done: (result: LevelResult) => void) => (() => void) | void,
): Promise<LevelResult> {
  return new Promise<LevelResult>((resolve, reject) => {
    let settled = false;
    let cleanup: (() => void) | void;

    const finish = (fn: () => void) => {
      if (settled) return;
      settled = true;
      ctx.signal.removeEventListener("abort", onAbort);
      cleanup?.();
      fn();
    };
    const onAbort = () =>
      finish(() => reject(new DOMException("level aborted", "AbortError")));

    if (ctx.signal.aborted) return onAbort();
    ctx.signal.addEventListener("abort", onAbort, { once: true });

    const done = (result: LevelResult) => finish(() => resolve(result));
    cleanup = setup(done) ?? undefined;
  });
}

/** A prompt line styled per the current act (`.prompt` reads act tokens). */
export function promptEl(text: string): HTMLElement {
  const p = el("div", "prompt");
  p.textContent = text;
  return p;
}

export interface Tile {
  /** decorative glyph — the caption carries the ontological meaning */
  emoji: string;
  /** short clinical label, the way the machine would catalogue a specimen */
  cap: string;
}

export interface Grid {
  root: HTMLElement;
  /** live set of selected tile indices (0-based), mutated as the user toggles */
  selected: Set<number>;
}

/**
 * A 3×3 CAPTCHA-style selection grid (SPEC §3, L02/L03). Tiles are emoji + caption
 * rather than photos — the joke here is ontological, not photographic, and the build
 * stays self-contained (no asset pipeline). Toggling is pointer + keyboard accessible.
 */
export function buildGrid(tiles: Tile[]): Grid {
  const root = el("div", "grid");
  root.setAttribute("role", "group");
  const selected = new Set<number>();

  tiles.forEach((tile, i) => {
    const cell = el("button", "tile");
    cell.type = "button";
    cell.setAttribute("aria-pressed", "false");
    cell.setAttribute("aria-label", tile.cap);

    const check = el("span", "tile__check");
    check.setAttribute("aria-hidden", "true");
    check.textContent = "✓";
    const glyph = el("span", "tile__emoji");
    glyph.setAttribute("aria-hidden", "true");
    glyph.textContent = tile.emoji;
    const cap = el("span", "tile__cap");
    cap.textContent = tile.cap;

    cell.append(check, glyph, cap);
    cell.addEventListener("click", () => {
      const on = cell.classList.toggle("tile--on");
      cell.setAttribute("aria-pressed", on ? "true" : "false");
      if (on) selected.add(i);
      else selected.delete(i);
    });

    root.append(cell);
  });

  return { root, selected };
}
